import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem
} from '@mui/material';
import { Edit, Delete, Add, MedicalServicesSharp, Search } from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import validator from 'validator';
import { useTheme } from '@mui/material';
import Spinner from '../components/Spinner';


const normalizeText = (text) => {
    return text
        ?.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

const PatientList = () => {
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [newPatient, setNewPatient] = useState({
        nombre: '',
        birthdate: '',
        sexo: '',
        edocivil: '',
        numid: '',
        domicilio: '',
        email: '',
        asegurador: '',
        tel: '',
        notas: 'Atopías:\nHTA (Hipertensión Arterial):\nDM (Diabetes Mellitus):\nDislipidemia:\nTiroides:\nFumador:\nEtilismo:\n\nG P A C E O\nFUR (Fecha Última Regla):\nPlanificación:\nCirugías:\nVacuna contra el Papiloma:',
        proxcita: '',
        foto: '',
        aviso: ''
    });
    const [age, setAge] = useState('');
    const [emailError, setEmailError] = useState('');
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    //const [deletePatientId, setDeletePatientId] = useState(null);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [todayPatients, setTodayPatients] = useState([]);
    const [showTodayPatients, setShowTodayPatients] = useState(false);
    const [totalPatients, setTotalPatients] = useState(0);
    const [totalTodayPatients, setTotalTodayPatients] = useState(0); // Nuevo estado para el conteo de pacientes de hoy
    const [searchFilter, setSearchFilter] = useState('nombre'); // Nuevo estado para el filtro seleccionado
    const [duplicateNameError, setDuplicateNameError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isDuplicateName, setIsDuplicateName] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [patientToDelete, setPatientToDelete] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL;

    const handleRowClick = (params, isActionButton) => {
        if (isActionButton) {
            if (params.field === 'actions') {
                const action = params.value;
                if (action === 'Modificar') {
                    handleSelectPatient(params.row);
                } else if (action === 'Eliminar') {
                    handleDeleteClickOpen(params.row.id);
                } else if (action === 'Historial') {
                    navigate(`/historias/${params.row.id}`);
                }
            }
        } else {
            setSelectedPatient(params.row);
            setOpenViewModal(true);
        }
    };

    const fetchPatients = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/identificacion`);
            const data = response.data;
    
            // Mapear los pacientes para formatear la fecha de nacimiento
            const patientsWithFormattedDates = data.map((patient, index) => ({
                ...patient,
                id: patient.idpac || `temp-${index}`, // Usar idpac como ID o generar uno temporal
                birthdate: patient.birthdate ? new Date(patient.birthdate).toISOString().split('T')[0] : '' // Formatear fecha
            }));
    
            setPatients(patientsWithFormattedDates); // Establecer los pacientes en el estado
        } catch (error) {
            console.error('Error al obtener los datos de los pacientes:', error);
        }
    }, [API_URL]);

    const fetchTodayPatients = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/pacientes-historias-hoy`);
           const todayPatientsWithIds = response.data.map((patient, index) => ({
             ...patient,
             id: patient.idpac || `today-temp-${index}`,
           }));
       
           setTodayPatients(todayPatientsWithIds);
           setTotalTodayPatients(todayPatientsWithIds.length); // Actualizar el conteo de pacientes de hoy
        } catch (error) {
            console.error('Error al obtener los pacientes con historias creadas hoy:', error);
        }
    }, [API_URL]);

    useEffect(() => {
        const fetchData = async () => {
          setIsLoading(true);
          try {
            await fetchPatients();
            await fetchTodayPatients();
            const totalResponse = await axios.get(`${API_URL}/api/identificacion/total`);
            setTotalPatients(totalResponse.data.total);
          } catch (error) {
            console.error('Error obteniendo datos:', error);
          } finally {
            setIsLoading(false);
          }
        };
        fetchData();
      }, [fetchPatients, fetchTodayPatients, API_URL]);
    
      useEffect(() => {
        fetchPatients();
      }, [fetchPatients]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setNewPatient({ ...newPatient, [name]: value });
        
        if (name === 'nombre' && value.trim()) {
            // Verificar duplicados en tiempo real
            const normalizedNewName = normalizeText(value);
            const isDuplicate = patients.some(patient => 
                patient.id !== selectedPatient?.id && 
                normalizeText(patient.nombre) === normalizedNewName
            );
            setIsDuplicateName(isDuplicate);
            setDuplicateNameError(isDuplicate ? 'Este nombre ya existe en la base de datos' : '');
        }
        setNewPatient({ ...newPatient, [name]: value });
        if (name === 'birthdate') {
            const birthDate = new Date(value);
            const today = new Date();
            
            let years = today.getFullYear() - birthDate.getFullYear();
            let months = today.getMonth() - birthDate.getMonth();
            let days = today.getDate() - birthDate.getDate();
            
            // Ajustar meses y años si los días son negativos
            if (days < 0) {
                months--;
                // Obtener el último día del mes anterior
                const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                days += lastMonth.getDate();
            }
            
            // Ajustar años si los meses son negativos
            if (months < 0) {
                years--;
                months = 12 + months;
            }
            
            // Formatear el texto de edad
            let ageText = '';
            if (years > 0) {
                ageText = `${years} ${years === 1 ? 'año' : 'años'}`;
                if (months > 0) {
                    ageText += ` y ${months} ${months === 1 ? 'mes' : 'meses'}`;
                }
                if (days > 0) {
                    ageText += ` y ${days} ${days === 1 ? 'día' : 'días'}`;
                }
            } else if (months > 0) {
                ageText = `${months} ${months === 1 ? 'mes' : 'meses'}`;
                if (days > 0) {
                    ageText += ` y ${days} ${days === 1 ? 'día' : 'días'}`;
                }
            } else {
                ageText = `${days} ${days === 1 ? 'día' : 'días'}`;
            }
            
            setAge(ageText);
        }
        if (name === 'email') {
            if (validator.isEmail(value) || value === '') {
                setEmailError('');
            } else {
                setEmailError('Email no es válido');
            }
        }
    };

    const mapToDatabaseValues = (patient) => {
        const sexMap = {
            Masculino: 'M',
            Femenino: 'F'
        };
        const civilStatusMap = {
            Casado: 'C',
            Divorciado: 'D',
            Soltero: 'S',
            'Union Libre': 'U',
            Viudo: 'V'
        };
        return {
            ...patient,
            sexo: sexMap[patient.sexo] || patient.sexo,
            edocivil: civilStatusMap[patient.edocivil] || patient.edocivil
        };
    };

    const mapFromDatabaseValues = (patient) => {
        const sexMap = {
            M: 'Masculino',
            F: 'Femenino'
        };
        const civilStatusMap = {
            C: 'Casado',
            D: 'Divorciado',
            S: 'Soltero',
            U: 'Union Libre',
            V: 'Viudo'
        };
        return {
            ...patient,
            sexo: sexMap[patient.sexo] || patient.sexo,
            edocivil: civilStatusMap[patient.edocivil] || patient.edocivil
        };
    };

    const handleAddPatient = async () => {
        if (isDuplicateName) {
            setErrorMessage('No se puede guardar: Ya existe un paciente con este nombre.');
            setShowErrorDialog(true);
            return;
        }
    
        const patientForDB = mapToDatabaseValues({
            ...newPatient,
            birthdate: newPatient.birthdate || null
        });
        
        try {
            await axios.post(`${API_URL}/api/identificacion`, patientForDB);
            await fetchPatients();
            await fetchTodayPatients();
            setOpen(false);
            // Restablecer el formulario
            setNewPatient({
                // ... tu reset de formulario existente ...
            });
            setAge('');
        } catch (error) {
            if (error.response?.status === 400) {
                setErrorMessage(error.response.data.message);
                setShowErrorDialog(true);
            } else {
                console.error('Error al agregar el paciente:', error);
            }
        }
    };

        const handleSelectPatient = (patient) => {
            const mappedPatient = mapFromDatabaseValues(patient);
            setSelectedPatient(mappedPatient);
            setNewPatient({
                ...mappedPatient,
                email: mappedPatient.email || ''
            });
            if (mappedPatient.birthdate) {
                const birthDate = new Date(mappedPatient.birthdate);
                const today = new Date();
                
                let years = today.getFullYear() - birthDate.getFullYear();
                let months = today.getMonth() - birthDate.getMonth();
                let days = today.getDate() - birthDate.getDate();
                
                if (days < 0) {
                    months--;
                    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                    days += lastMonth.getDate();
                }
                
                if (months < 0) {
                    years--;
                    months = 12 + months;
                }
                
                let ageText = '';
                if (years > 0) {
                    ageText = `${years} ${years === 1 ? 'año' : 'años'}`;
                    if (months > 0) {
                        ageText += ` y ${months} ${months === 1 ? 'mes' : 'meses'}`;
                    }
                    if (days > 0) {
                        ageText += ` y ${days} ${days === 1 ? 'día' : 'días'}`;
                    }
                } else if (months > 0) {
                    ageText = `${months} ${months === 1 ? 'mes' : 'meses'}`;
                    if (days > 0) {
                        ageText += ` y ${days} ${days === 1 ? 'día' : 'días'}`;
                    }
                } else {
                    ageText = `${days} ${days === 1 ? 'día' : 'días'}`;
                }
                
                setAge(ageText);
                setNewPatient(prevState => ({ ...prevState, birthdate: birthDate.toISOString().slice(0, 10) }));
            } else {
                setAge('');
            }
            setOpen(true);
        };

    const handleUpdatePatient = async () => {
        const patientForDB = mapToDatabaseValues({
            ...newPatient,
            birthdate: newPatient.birthdate || null // Convertir vacío a null
        });
        try {
            await axios.put(`${API_URL}/api/identificacion/${patientForDB.id}`, patientForDB);
            setPatients(patients.map(patient => patient.id === newPatient.id ? newPatient : patient));
        } catch (error) {
            console.error('Error al modificar el paciente:', error);
        }
        setSelectedPatient(null);
        setNewPatient({
            nombre: '',
            birthdate: '',
            sexo: '',
            edocivil: '',
            numid: '',domicilio: '',
            email: '',
            asegurador: '',
            tel: '',
            notas: 'Atopías:\nHTA (Hipertensión Arterial):\nDM (Diabetes Mellitus):\nDislipidemia:\nTiroides:\nFumador:\nEtilismo:\n\nG P A C E O\nFUR (Fecha Última Regla):\nPlanificación:\nCirugías:\nVacuna contra el Papiloma:',
            proxcita: '',
            foto: '',
            aviso: ''
        });
        setOpen(false);
    };
    
    const handleDeletePatient = async () => {
        try {
            await axios.delete(`${API_URL}/api/identificacion/${patientToDelete.id}`);
            setPatients(patients.filter(patient => patient.id !== patientToDelete.id));
            setTotalPatients(prev => prev - 1);
        } catch (error) {
            console.error('Error al eliminar el paciente:', error);
        }
        setPatientToDelete(null);
        setDeleteDialogOpen(false);
    };
    
        const handleClickOpen = () => {
            setSelectedPatient(null);
            setNewPatient({
                nombre: '',
                birthdate: '',
                sexo: '',
                edocivil: '',
                numid: '',
                domicilio: '',
                email: '',
                asegurador: '',
                tel: '',
                notas: 'Atopías:\nHTA (Hipertensión Arterial):\nDM (Diabetes Mellitus):\nDislipidemia:\nTiroides:\nFumador:\nEtilismo:\n\nG P A C E O\nFUR (Fecha Última Regla):\nPlanificación:\nCirugías:\nVacuna contra el Papiloma:',
                proxcita: '',
                foto: '',
                aviso: ''
            });
            setAge('');
            setOpen(true);
        };
    
        const handleClose = () => {
            setOpen(false);
        };
    
        const handleDeleteClickOpen = (id) => {
            const patient = patients.find(p => p.id === id);
            setPatientToDelete(patient);
            setDeleteDialogOpen(true);
        };
    
        const handleDeleteClickClose = () => {
            setDeleteDialogOpen(false);
        };
    
        const columns = [
            { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 350 },
            { 
                field: 'birthdate', 
                headerName: 'Nacimiento', 
                flex: 1, 
                minWidth: 110,
              },
            { field: 'sexo', headerName: 'Sexo', width: 80 },
            { field: 'edocivil', headerName: 'Estado Civil', flex: 1, minWidth: 120 },
            { field: 'domicilio', headerName: 'Domicilio', flex: 2, minWidth: 200 },
            {
                field: 'actions',
                headerName: 'Acciones',
                width: 330,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Edit />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectPatient(params.row);
                            }}
                        >
                            Editar
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            startIcon={<Delete />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClickOpen(params.row.id);
                            }}
                        >
                            Borrar
                        </Button>
                        <Button
                            variant="contained"
                            color="info"
                            size="small"
                            startIcon={<MedicalServicesSharp />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/historias/${params.row.id}`);
                            }}
                        >
                            Historial
                        </Button>
                    </Box>
                )
            }
        ];
    
        const handleFilterChange = (e) => {
            setSearchFilter(e.target.value);
        };
        
        // Reemplazar la función filteredPatients existente
const filteredPatients = useMemo(() => {
    return (showTodayPatients ? todayPatients : patients).filter(patient => {
        const value = normalizeText(patient[searchFilter]?.toString());
        const searchValue = normalizeText(search);

        // Mapeo para el filtro de sexo
        const sexMap = {
            masculino: 'm',
            femenino: 'f',
            m: 'm',
            f: 'f'
        };

        // Mapeo para el filtro de estado civil
        const civilStatusMap = {
            casado: 'c',
            divorciado: 'd',
            soltero: 's',
            'union libre': 'u',
            viudo: 'v',
            c: 'c',
            d: 'd',
            s: 's',
            u: 'u',
            v: 'v'
        };

        // Verificar si el filtro es por sexo o estado civil
        if (searchFilter === 'sexo') {
            return sexMap[searchValue] === value;
        } else if (searchFilter === 'edocivil') {
            return civilStatusMap[searchValue] === value;
        }

        // Filtro genérico para otros campos
        return value?.includes(searchValue);
    });
}, [patients, search, searchFilter, showTodayPatients, todayPatients]);
    
        const [paginationModel, setPaginationModel] = useState({
            pageSize: 5,
            page: 0,
        });
    
        const theme = useTheme();
    
        
    
        const handleToggleTodayPatients = () => {
            setShowTodayPatients(!showTodayPatients);
        };
    
        return (
            <Box
                className="center-align"
                sx={{
                    display: 'flex',
                    padding: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    flexDirection: 'column'
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: { xs: 2, sm: 4 },
                        width: '100%',
                        maxWidth: '98vw',
                        overflowX: 'auto',
                        borderRadius: 2,
                        margin: 'auto',
                        boxSizing: 'border-box',
                        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                >
                    <Spinner isLoading={isLoading} message="Cargando pacientes..." />
                     <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                        Gestión de Pacientes ({showTodayPatients ? `${totalTodayPatients} registros de hoy` : `${totalPatients} registros`})
                    </Typography>
                    <Grid container spacing={3} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Buscar Paciente"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Filtrar por"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={searchFilter}
                            onChange={handleFilterChange}
                        >
                            <MenuItem value="nombre">Nombre</MenuItem>
                            <MenuItem value="sexo">Sexo</MenuItem>
                            <MenuItem value="edocivil">Estado Civil</MenuItem>
                            <MenuItem value="asegurador">Aseguradora</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="domicilio">Domicilio</MenuItem>
                            <MenuItem value="tel">Teléfonos</MenuItem>
                            <MenuItem value="numid">Num ID</MenuItem>
                            <MenuItem value="notas">Otros datos</MenuItem>
                        </TextField>
                     </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap:1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={handleClickOpen}
                                sx={{
                                    width: { xs: '20%', sm: 'auto' },
                                    order: { xs: 1, sm: 2 }
                                }}
                            >
                                Agregar Paciente
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<Search />}
                                onClick={handleToggleTodayPatients}
                                sx={{
                                    width: { xs: '20%', sm: 'auto' },
                                    order: { xs: 2, sm: 0 }
                                }}
                            >
                                {showTodayPatients ? `Mostrar Todos (${totalPatients})` : `Pacientes de Hoy (${totalTodayPatients})`}
                            </Button>                            
                        </Grid>
                    </Grid>
                    <div style={{ height: '60vh', width: '100%', minHeight: '300px' }}>
                    {!isLoading && (
                        <DataGrid
                            key={patients.length}
                            rows={filteredPatients}
                            columns={columns}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[5]}
                            onRowClick={(params) => handleRowClick(params, false)}
                            disableSelectionOnClick
                            components={{
                                Toolbar: GridToolbar
                            }}
                            density="compact"
                            autoPageSize
                        />
                    )}
                    </div>
                    <Dialog 
                        open={open} 
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                            handleClose();
                            }
                        }}
                        aria-labelledby="form-dialog-title"
                        disableEscapeKeyDown
                        >
                        <DialogTitle sx={{ fontWeight: 'bold', color: theme.palette.primary.main }} id="form-dialog-title">{selectedPatient ? 'Modificar Paciente' : 'Agregar Paciente'}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {selectedPatient ? 'Modificar los detalles del paciente.' : 'Ingrese los detalles del nuevo paciente.'}
                            </DialogContentText>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Nombre"
                                type="text"
                                fullWidth
                                name="nombre"
                                value={newPatient.nombre}
                                onChange={handleInputChange}
                                required
                                error={!!duplicateNameError || !newPatient.nombre || isDuplicateName}
                                helperText={
                                    duplicateNameError || 
                                    (!newPatient.nombre && "El nombre es obligatorio") ||
                                    (isDuplicateName && "Este nombre ya existe en la base de datos")
                                }
                            />
                            <TextField
                                margin="dense"
                                label="Fecha de Nacimiento"
                                type="date"
                                fullWidth
                                name="birthdate"
                                value={newPatient.birthdate}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                margin="dense"
                                label="Edad"
                                type="text"
                                fullWidth
                                name="edad"
                                value={age}
                                disabled
                            />
                            <TextField
                                select
                                margin="dense"
                                label="Sexo"fullWidth
                                name="sexo"
                                value={newPatient.sexo}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="Masculino">Masculino</MenuItem>
                                <MenuItem value="Femenino">Femenino</MenuItem>
                            </TextField>
                            <TextField
                                select
                                margin="dense"
                                label="Estado Civil"
                                fullWidth
                                name="edocivil"
                                value={newPatient.edocivil}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="Casado">Casado(a)</MenuItem>
                                <MenuItem value="Divorciado">Divorciado(a)</MenuItem>
                                <MenuItem value="Soltero">Soltero(a)</MenuItem>
                                <MenuItem value="Union Libre">Unión Libre(a)</MenuItem>
                                <MenuItem value="Viudo">Viudo(a)</MenuItem>
                            </TextField>
                            <TextField
                                margin="dense"
                                label="N° Identificación"
                                type="number"
                                fullWidth
                                name="numid"
                                value={newPatient.numid}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                label="Domicilio"
                                type="text"
                                fullWidth
                                name="domicilio"
                                value={newPatient.domicilio}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                label="Email"
                                type="email"
                                fullWidth
                                name="email"
                                value={newPatient.email}
                                onChange={(e) => {
                                    handleInputChange(e);
                                    if (validator.isEmail(e.target.value) || e.target.value === '') {
                                        setEmailError('');
                                    } else {
                                        setEmailError('Email no es válido');
                                    }
                                }}
                                error={emailError !== ''}
                                helperText={emailError}
                            />
                            <TextField
                                margin="dense"
                                label="Teléfono"
                                type="tel"
                                fullWidth
                                name="tel"
                                value={newPatient.tel}
                                onChange={handleInputChange}
                            />
                            <TextField
                                margin="dense"
                                label="Notas"
                                type="text"
                                fullWidth
                                name="notas"
                                value={newPatient.notas}
                                onChange={handleInputChange}
                                multiline
                                rows={12}
                            />
                            <TextField
                                margin="dense"
                                label="Próxima Cita"
                                type="date"
                                fullWidth
                                name="proxcita"
                                value={newPatient.proxcita}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="primary">
                                Cancelar
                            </Button>
                            <Button onClick={selectedPatient ? handleUpdatePatient : handleAddPatient} color="primary">
                                {selectedPatient ? 'Modificar' : 'Agregar'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog 
                        open={deleteDialogOpen} 
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                            handleDeleteClickClose();
                            }
                        }}
                        aria-labelledby="delete-dialog-title"
                        disableEscapeKeyDown
                        >
                        <DialogTitle id="delete-dialog-title">Confirmar Eliminación</DialogTitle>
                        <DialogContent>
                        <DialogContentText>
                            ¿Estás seguro de que deseas eliminar al paciente{' '}
                            <Typography
                                component="span"
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#1a237e', // azul oscuro
                                    display: 'inline'
                                }}
                            >
                                {patientToDelete?.nombre}
                            </Typography>
                            ?
                        </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleDeleteClickClose} color="primary">
                                Cancelar
                            </Button>
                            <Button onClick={handleDeletePatient} color="secondary">
                                Eliminar
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={openViewModal}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                            setOpenViewModal(false);
                            }
                        }}
                        maxWidth="lg"
                        fullWidth
                        disableEscapeKeyDown
                        >
                        <DialogTitle sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>Detalles del Paciente</DialogTitle>
                        <DialogContent dividers>
                            {selectedPatient && (
                                <Box sx={{ p: 2 }}>
                                    <DialogContentText>
                                        <strong>Nombre:</strong> {selectedPatient?.nombre}<br />
                                        <strong>Fecha de Nacimiento:</strong> {selectedPatient?.birthdate?.slice(0, 10)}<br />
                                        <strong>Sexo:</strong> {selectedPatient?.sexo}<br />
                                        <strong>Estado Civil:</strong> {selectedPatient?.edocivil}<br />
                                        <strong>N° Identificación:</strong> {selectedPatient?.numid}<br />
                                        <strong>Domicilio:</strong> {selectedPatient?.domicilio}<br />
                                        <strong>Email:</strong> {selectedPatient?.email}<br />
                                        <strong>Teléfono:</strong> {selectedPatient?.tel}<br />
                                        <strong>Notas:</strong> {selectedPatient?.notas}<br />
                                        <strong>Próxima Cita:</strong> {selectedPatient?.proxcita}<br />
                                    </DialogContentText>
                                    <DialogActions>
                                        <Button onClick={() => setOpenViewModal(false)} color="primary">
                                            Cerrar
                                        </Button>
                                    </DialogActions>
                                </Box>
                            )}
                        </DialogContent>
                    </Dialog>
                    <Dialog
                        open={showErrorDialog}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                            setShowErrorDialog(false);
                            }
                        }}
                        aria-labelledby="error-dialog-title"
                        disableEscapeKeyDown
                        >
                        <DialogTitle id="error-dialog-title" sx={{ color: 'error.main' }}>
                            Error al guardar
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {errorMessage}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowErrorDialog(false)} color="primary">
                                Entendido
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Box>
        );
    };

    export default PatientList;